/**
 * Access Control admin page.
 *
 * Owner/Admin-only. Lists client users and lets you toggle their
 * fine-grained scopes (Compliance Dashboard, Policies, CAIQ, Reports,
 * Accessibility Documents).
 *
 * Scope changes take effect on the client's very next request — no
 * re-login needed. Scopes live on `client_users.scopes` and are
 * re-loaded on every scope-gated call.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Shield, Save, User, Mail, Building2, Clock } from 'lucide-react';
import { Card, Badge } from '../components/ui-custom/ComplianceUI';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AccessControlPage() {
  const [scopes, setScopes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  // Track per-user dirty scope sets (Set<scopeKey>) — only saved rows commit.
  const [dirtyScopes, setDirtyScopes] = useState({});   // { userId: Set<scope> }
  // Per-user, per-scope expiry map. Values are ISO datetimes (yyyy-mm-ddT...).
  // A missing/undefined value = permanent; an empty string = removed by owner.
  const [dirtyExpiry, setDirtyExpiry] = useState({});   // { userId: { scope: iso } }
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, uRes] = await Promise.all([
        fetch(`${API_URL}/api/access-control/scopes`, { credentials: 'include' }),
        fetch(`${API_URL}/api/access-control/client-users`, { credentials: 'include' }),
      ]);
      if (!sRes.ok || !uRes.ok) {
        throw new Error('Failed to load access-control data');
      }
      const s = await sRes.json();
      const u = await uRes.json();
      setScopes(s.scopes || []);
      setUsers(u.users || []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const effectiveScopeSet = (user) => {
    if (dirtyScopes[user.id]) return dirtyScopes[user.id];
    return new Set(user.scopes || []);
  };

  const effectiveExpiryFor = (user, scopeKey) => {
    // Per-user dirty overrides win. Otherwise, look at server-provided expiries.
    const dirty = dirtyExpiry[user.id];
    if (dirty && Object.prototype.hasOwnProperty.call(dirty, scopeKey)) {
      return dirty[scopeKey] || '';
    }
    return user.scopes_expires_at?.[scopeKey] || '';
  };

  const toggle = (user, scopeKey) => {
    const current = new Set(effectiveScopeSet(user));
    if (current.has(scopeKey)) {
      current.delete(scopeKey);
      // Turning a scope off also clears any expiry-in-progress for it
      setDirtyExpiry({
        ...dirtyExpiry,
        [user.id]: { ...(dirtyExpiry[user.id] || {}), [scopeKey]: '' },
      });
    } else {
      current.add(scopeKey);
    }
    setDirtyScopes({ ...dirtyScopes, [user.id]: current });
  };

  const setExpiry = (user, scopeKey, isoDate) => {
    // isoDate is either "" (clear) or "YYYY-MM-DD" from the date input.
    // Convert to a full ISO 8601 UTC string so the backend can parse it.
    const iso = isoDate ? new Date(`${isoDate}T23:59:59Z`).toISOString() : '';
    setDirtyExpiry({
      ...dirtyExpiry,
      [user.id]: { ...(dirtyExpiry[user.id] || {}), [scopeKey]: iso },
    });
    // Editing an expiry also marks the row dirty so the Save button lights up
    if (!dirtyScopes[user.id]) {
      setDirtyScopes({ ...dirtyScopes, [user.id]: effectiveScopeSet(user) });
    }
  };

  const save = async (user) => {
    const set = dirtyScopes[user.id];
    if (!set) return;
    setSavingId(user.id);
    try {
      // Build the expiry map ONLY for currently-granted scopes.
      const grantedArr = Array.from(set);
      const expiryMap = {};
      for (const s of grantedArr) {
        const iso = effectiveExpiryFor(user, s);
        if (iso) expiryMap[s] = iso;
      }

      const res = await fetch(
        `${API_URL}/api/access-control/client-users/${user.id}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            scopes: grantedArr,
            scope_expires_at: expiryMap,
          }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      toast.success(`Updated access for ${user.email}`);
      // Reflect the saved state in the users array and clear dirty flags
      setUsers(users.map((u) =>
        u.id === user.id
          ? { ...u, scopes: grantedArr, scopes_expires_at: expiryMap }
          : u
      ));
      const nd = { ...dirtyScopes };
      delete nd[user.id];
      setDirtyScopes(nd);
      const ne = { ...dirtyExpiry };
      delete ne[user.id];
      setDirtyExpiry(ne);
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Loader2 className="spin" size={24} />
      </div>
    );
  }

  return (
    <div className="access-control-page" data-testid="access-control-page">
      <div className="page-header">
        <div>
          <h1><Shield size={24} /> Access Control</h1>
          <p>
            Grant fine-grained access to specific sections for each external client user.
            Perfect for prospects who should only see WCAG/VPAT, or clients under NDA who
            get the full portal.
          </p>
        </div>
      </div>

      {users.length === 0 ? (
        <Card style={{ padding: 40, textAlign: 'center' }} data-testid="access-control-empty">
          <User size={40} color="#6b7280" />
          <h3 style={{ marginTop: 12 }}>No client users yet</h3>
          <p style={{ color: '#94a3b8' }}>
            Invite external clients from the Clients page, then come back to set their access.
          </p>
        </Card>
      ) : (
        <div className="ac-user-list">
          {users.map((user) => {
            const currentSet = effectiveScopeSet(user);
            const isDirty = !!dirtyScopes[user.id] || !!dirtyExpiry[user.id];
            const isSaving = savingId === user.id;
            return (
              <Card
                key={user.id}
                data-testid={`ac-user-${user.id}`}
                style={{ padding: 20, marginBottom: 16 }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <User size={16} color="#94a3b8" />
                      <strong>{user.name || '(no name)'}</strong>
                      <Badge color={user.is_active ? '#059669' : '#6b7280'}>
                        {user.is_active ? 'active' : 'disabled'}
                      </Badge>
                      <Badge color="#7c3aed">{user.role}</Badge>
                    </div>
                    <div style={{ fontSize: 13, color: '#94a3b8', display: 'flex', gap: 16 }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Mail size={13} /> {user.email}
                      </span>
                      {user.company_name && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Building2 size={13} /> {user.company_name}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => save(user)}
                    disabled={!isDirty || isSaving}
                    data-testid={`ac-save-${user.id}`}
                    style={{
                      padding: '8px 14px', borderRadius: 6,
                      background: isDirty ? '#2563eb' : '#334155',
                      color: '#fff', border: 'none',
                      cursor: (isDirty && !isSaving) ? 'pointer' : 'not-allowed',
                      display: 'flex', alignItems: 'center', gap: 6,
                      opacity: (isDirty && !isSaving) ? 1 : 0.55,
                    }}
                  >
                    {isSaving ? <Loader2 size={14} className="spin" /> : <Save size={14} />}
                    {isSaving ? 'Saving…' : isDirty ? 'Save changes' : 'Saved'}
                  </button>
                </div>

                <div
                  style={{
                    marginTop: 16, display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10,
                  }}
                >
                  {scopes.map((s) => {
                    const on = currentSet.has(s.key);
                    const expiryIso = effectiveExpiryFor(user, s.key);
                    // Convert ISO to YYYY-MM-DD for the <input type="date">
                    const expiryDateValue = expiryIso
                      ? new Date(expiryIso).toISOString().slice(0, 10)
                      : '';
                    return (
                      <label
                        key={s.key}
                        data-testid={`ac-scope-${user.id}-${s.key}`}
                        style={{
                          display: 'flex', alignItems: 'flex-start', gap: 10,
                          padding: 10, borderRadius: 6, cursor: 'pointer',
                          background: on ? 'rgba(37,99,235,0.10)' : 'rgba(255,255,255,0.03)',
                          border: `1px solid ${on ? 'rgba(37,99,235,0.35)' : 'rgba(255,255,255,0.08)'}`,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => toggle(user, s.key)}
                          style={{ marginTop: 3, cursor: 'pointer' }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{s.label}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                            {s.description}
                          </div>
                          {on && (
                            <div
                              style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}
                              onClick={(e) => e.preventDefault()}
                              data-testid={`ac-expiry-row-${user.id}-${s.key}`}
                            >
                              <Clock size={11} color="#94a3b8" />
                              <span style={{ fontSize: 11, color: '#94a3b8' }}>
                                Expires:
                              </span>
                              <input
                                type="date"
                                value={expiryDateValue}
                                onChange={(e) => setExpiry(user, s.key, e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`ac-expiry-${user.id}-${s.key}`}
                                style={{
                                  fontSize: 11, padding: '3px 6px', borderRadius: 4,
                                  background: 'rgba(255,255,255,0.05)',
                                  color: '#cbd5e1', border: '1px solid rgba(255,255,255,0.10)',
                                }}
                              />
                              {expiryDateValue && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.preventDefault(); setExpiry(user, s.key, ''); }}
                                  data-testid={`ac-expiry-clear-${user.id}-${s.key}`}
                                  style={{
                                    background: 'transparent', border: 'none',
                                    color: '#94a3b8', cursor: 'pointer', fontSize: 11,
                                    padding: 0, textDecoration: 'underline',
                                  }}
                                >
                                  clear
                                </button>
                              )}
                              {!expiryDateValue && (
                                <span style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>permanent</span>
                              )}
                            </div>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
